<template lang="pug">
.col.vuetable-wrapper
  .loader.text-center(v-if="loading")
    i.fas.fa-spinner.fa-spin.fa-2x
  vuetable.vuetable.w-100(ref="vuetable"
                          v-show="!loading"
                          v-if="isUserPresent"
                          :api-url="tableApiUrl"
                          :http-options="httpOptions"
                          :http-fetch="axiosFetch"
                          :css="css.table"
                          :fields="tableFields"
                          :sort-order="defaultSortOrder"
                          :initialPage="pageFromRoute"
                          @vuetable:pagination-data="onPaginationData"
                          @vuetable:load-success="onLoadSuccess"
                          @vuetable:loading="loading = true"
                          @vuetable:loaded="loading = false"
                          :no-data-template="$t('property.no_data_available')"
                          )
    template(slot="existing", slot-scope="props")
      | {{ $t('property_list_page.existing_column.' + props.rowData.existing) }}

    template(slot="name", slot-scope="props")
      router-link.property-link(
        :id="'property-link-' + props.rowData.id"
        :to="{ name: 'PropertyPage', params: { id: props.rowData.id } }"
      )
        b-tooltip(
          :target="'property-link-' + props.rowData.id"
          placement="left"
          :title="tooltip.rentAssessmentPage"
        )
        | {{ props.rowData.name }}

    template(slot="address", slot-scope="props")
      | {{ props.rowData.post_code }} {{ props.rowData.address_line1 }} {{ props.rowData.address_line2 }}

    template(slot="area", slot-scope="props")
      | {{ $n(props.rowData.area) }}

    template(slot="building_structure", slot-scope="props")
      | {{ $t('building_structure.' + props.rowData.building_structure) }}

    template(slot="parking", slot-scope="props")
      | {{ $t('property_list_page.parking_column.' + props.rowData.parking) }}

    template(slot="actions", slot-scope="props")
      .property-actions(:class="[isSuperadmin ? 'text-right' : 'user-actions-display']")
        a.mr-3(
          href="#"
          :id="'facilities-' + props.rowData.id"
          @click.prevent="setRoomsModalData(props.rowData)"
        )
          b-tooltip(
            :target="'facilities-' + props.rowData.id"
            placement="left"
            :title="tooltip.facilities"
          )
          i.fas.fa-info-circle.icon-action

        button.mr-3.gallery(
          @click.prevent="showImages(props.rowData)"
        )
          i.fas.fa-camera.icon-action

        router-link.mr-3(
          :id="'rent-assessment-' + props.rowData.id "
          :to="{ name: 'PropertyPage', params: { id: props.rowData.id } }"
        )
          b-tooltip(
            :target="'rent-assessment-' + props.rowData.id "
            placement="left"
            :title="tooltip.rentAssessmentPage"
          )
          i.fas.fa-chart-bar.icon-action

        b-dropdown(right :disabled="isUserReadOnly")
          template(slot="button-content")
            i.fas.fa-ellipsis-h
          b-dropdown-item(:to="{ name: 'PropertyEdit', params: { id: props.rowData.id } }") {{ $t('property_list_page.edit') }}
          b-dropdown-item(:to="{ name: 'PropertyCopy', params: { id: props.rowData.id } }") {{ $t('property_list_page.copy') }}
          b-dropdown-item(
            @click="openModalDeleteProperty(props.rowData.id, props.rowData.name)"
            to="#"
            v-b-modal.modal-delete-property
          ) {{ $t('property_list_page.delete') }}
  delete-property-modal(
    :name="deletePropertyModal.name"
    @cancelDelete="cancelDeleteProperty"
    @acceptDelete="deleteProperty"
  )

  .pagination-wrapper
    .pagination-data(v-if="!loading")
        span {{ firstRecord }}-{{ lastRecord }}
        span /{{ this.pagination.total }}
    vuetable-pagination(ref="pagination"
                        v-show="!loading"
                        :css="css.pagination"
                        @vuetable-pagination:change-page="onChangePage")

  rooms-modal(:data="roomsModalData")
  image-gallery(
    v-if="showGallery"
    :property="currentProperty"
    @closeModal="closeGallery"
  )
</template>

<script>
import { api } from '@/api'
import Vuetable from 'vuetable-2/src/components/Vuetable'
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination'
import RoomsModal from '@/pages/Property/List/rooms-modal'
import DeletePropertyModal from '@/pages/Property/List/delete-property-modal'
import ImageGallery from '@/components/shared/image-gallery-modal'
import { getApiPath } from '@/helpers/api.js'
import { find, isEmpty } from 'lodash'
import currentUserMixin from '@/mixins/current-user'
import { mapState } from 'vuex'

export default {
  components: {
    Vuetable,
    VuetablePagination,
    RoomsModal,
    DeletePropertyModal,
    ImageGallery
  },


  mixins: [
    currentUserMixin
  ],

  data() {
    return {
      tooltip: {
        facilities: this.$t('property.facilities'),
        rentAssessmentPage: this.$t('property.rent_assessment_page')
      },
      showGallery: false,
      deletePropertyModal: {
        id: null,
        name: ''
      },
      loading: true,
      deleting: false,
      css: {
        table: {
          ascendingIcon: 'fas fa-chevron-up',
          descendingIcon: 'fas fa-chevron-down'
        },
        pagination: {
          wrapperClass: 'vuetable-pagination',
          activeClass: 'btn pagination-button active',
          disabledClass: 'disabled',
          pageClass: 'btn pagination-button',
          linkClass: 'btn pagination-button',
          icons: {
            first: 'fas fa-angle-double-left',
            prev: 'fas fa-angle-left',
            next: 'fas fa-angle-right',
            last: 'fas fa-angle-double-right'
          }
        }
      },
      tableApiUrl: getApiPath(`/properties?search=${this.queryString()}`),
      httpOptions: {
        headers: api.defaults.headers.common
      },
      pagination: {
        total: 0,
        per_page: 30,
        current_page: this.$route.params.page,
        last_page: 1
      },
      roomsModalData: {
        id: null,
        name: '',
        address: ''
      },
      currentProperty: {}
    }
  },

  computed: {
    ...mapState(['currentUser']),

    superAdminFields() {
      if (this.isSuperadmin) {
        return [
          {
            name: 'id',
            title: () => this.$t('property_list_page.database_id'),
            sortField: 'id',
            titleClass: 'id-column'
          },
          {
            name: 'username',
            title: () => this.$t('property_list_page.property_username'),
            sortField: 'username',
            titleClass: 'username-column'
          },
          {
            name: 'organization',
            title: () => this.$t('property_list_page.user_organization'),
            sortField: 'organization',
            titleClass: 'organization-column'
          }
        ]
      } else {
        return [];
      }

    },

    isUserPresent() {
      return !isEmpty(this.currentUser)
    },

    tableFields() {
      return [
        {
          name: '__slot:existing',
          title: () => this.$t('property_list_page.existing'),
          sortField: 'existing',
          titleClass: 'type-column'
        },
        ...this.superAdminFields,
        {
          name: 'slug',
          title: () => this.$t('property.property_id'),
          sortField: 'slug',
          titleClass: 'slug-column',
          dataClass: 'slug-column'
        },
        {
          name: '__slot:name',
          title: () => this.$t('property.name'),
          sortField: 'name',
          titleClass: 'name-column',
          dataClass: 'data-name-column'
        },
        {
          name: '__slot:address',
          sortField: 'address_with_post_code',
          title: () => this.$t('property_list_page.address'),
          titleClass: 'address-column',
          dataClass: 'data-address-column'
        },
        {
          name: 'floors',
          title: () => this.$t('property.floors'),
          sortField: 'floors',
          titleClass: 'floors-column'
        },
        {
          name: '__slot:area',
          title: () => this.$t('property.area'),
          sortField: 'area',
          titleClass: 'area-column'
        },
        {
          name: 'age',
          sortField: 'built_date',
          title: () => this.$t('property.age'),
          titleClass: 'age-column'
        },
        {
          name: '__slot:building_structure',
          title: () => this.$t('property.building_structure'),
          sortField: 'building_structure',
          titleClass: 'structure-column'
        },
        {
          name: '__slot:parking',
          title: () => this.$t('property.parking'),
          sortField: 'parking',
          titleClass: 'parking-column'
        },
        {
          name: '__slot:actions',
          titleClass: 'actions-column'
        },
        {
          name: 'created_at',
          sortField: 'created_at',
          titleClass: 'created-at-column',
          dataClass: 'created-at-column'
        }
      ]
    },

    isSuperadmin() {
      return this.currentUser.role == 'superadmin'
    },

    defaultSortOrder() {
      const sortField = this.sortFieldFromRoute || 'created_at'
      const field = this.findFieldName(sortField)
      const direction = this.sortDirectionFromRoute || 'desc'
      return [
        {
          sortField,
          field,
          direction
        }
      ]
    },

    lastRecord() {
      const maxCount = this.pagination.current_page * this.pagination.per_page
      return Math.min(maxCount, this.pagination.total)
    },

    firstRecord() {
      return ((this.pagination.current_page - 1) * this.pagination.per_page + 1)
    },

    pageFromRoute() {
      return Number(this.$route.params.page)
    },

    sortFieldFromRoute() {
      return this.$route.params.sortField
    },

    sortDirectionFromRoute() {
      return this.$route.params.sortDirection
    }
  },

  watch: {
    $route() {
      this.updateApiUrl()
    },

    pageFromRoute(changedPage) {
      if (isEmpty(this.$refs.vuetable.tablePagination)) {
        return
      }
      this.$refs.vuetable.changePage(changedPage)
    }
  },

  methods: {
    isEmpty,

    axiosFetch(apiUrl, httpOptions) {
      return api.get(apiUrl, httpOptions)
    },

    findFieldName(fieldName) {
      return find(this.tableFields, { sortField: fieldName }).name
    },

    showImages(property) {
      this.currentProperty = property
      this.showGallery = true
      document.documentElement.style.overflow = 'hidden'
    },

    closeGallery() {
      this.showGallery = false
      document.documentElement.style.overflow = 'auto'
    },

    onPaginationData(paginationData) {
      this.$refs.pagination.setPaginationData(paginationData)
    },

    onChangePage(page) {
      if (typeof page == 'string') {
        page = this.prevOrNextPage(page)
      }
      this.updateSelfRoute({ page })
    },

    onLoadSuccess(response) {
      this.pagination.current_page = this.pageFromRoute || 1
      this.pagination.per_page = Number(response.headers['per-page'])
      this.pagination.total = Number(response.headers['total'])
      this.pagination.last_page = Math.ceil(
        response.headers['total'] / response.headers['per-page']
      )
    },

    transform(data) {
      return {
        links: {
          pagination: this.pagination
        },
        data: data.data
      };
    },

    setRoomsModalData(row) {
      this.roomsModalData = {
        id: row.id,
        name: row.name,
        address: row.address_line1
      }

      this.$bvModal.show('rooms-modal')
    },

    openModalDeleteProperty(id, name) {
      this.deletePropertyModal.id = id
      this.deletePropertyModal.name = name
    },

    cancelDeleteProperty() {
      this.deletePropertyModal.id = null
      this.deletePropertyModal.name = ''
    },

    deleteProperty() {
      this.$emit('deletingProperty')
      api.delete(`/properties/${this.deletePropertyModal.id}`).then(resp => {
        if (resp.data.success) {
          this.$emit('deletedProperty')
          this.$refs.vuetable.refresh()
        }
      })
    },

    updateApiUrl() {
      this.tableApiUrl = getApiPath(`/properties?search=${this.queryString()}`)
      this.$emit('change', this.queryString())
    },

    queryString() {
      if (this.$route.query.search) {
        return this.$route.query.search
      } else {
        return ''
      }
    },

    prevOrNextPage(page) {
      if (page == 'prev') {
        return Number(this.$route.params.page) - 1
      } else {
        return Number(this.$route.params.page) + 1
      }
    },

    getSortParam() {
      const sortQueryValue = this.$refs.vuetable.getDefaultSortParam()
      this.updateSortParam(sortQueryValue)
      return sortQueryValue
    },

    updateSortParam(query) {
      const [sortField, sortDirection] = query.split('|')
      let page = this.pageFromRoute
      if (this.sortFieldFromRoute !== sortField || this.sortDirectionFromRoute !== sortDirection) {
        page = 1
      }
      this.updateSelfRoute({ page, sortField, sortDirection })
    },

    updateSelfRoute(paramsToUpdate) {
      const params = { ...this.$route.params, ...paramsToUpdate }
      const query = this.$route.query
      this.$router.push({ name: 'PropertyList', params, query })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .vuetable-pagination {
  text-align: center;

  a {
    cursor: pointer;
    font-size: 14px;
  }
}

::v-deep .vuetable {
  min-width: 1000px !important;

  th {
    color: #7c91a7;
    font-size: 12px;
    padding: 0 10px 20px 0;
    white-space: nowrap;

    &:hover {
      color: #009cea !important;
    }

    &:first-of-type {
      padding-left: 0;
    }

    .sort-icon {
      float: none !important;
      font-family: "Font Awesome 5 Free", sans-serif;
      font-size: 10px;
      font-style: normal;
      font-variant: normal;
      font-weight: 900;
      line-height: 1;
      margin-left: 2px;
      text-rendering: auto;

      &::before {
        content: "\f078";
      }

      &.fa-chevron-up::before {
        content: "\f077";
      }
    }

    &.type-column {
      min-width: 91px;
    }

    &.id-column {
      min-width: 70px;
    }

    &.name-column {
      min-width: 110px;
    }

    &.address-column {
      min-width: 128px;
    }

    &.floors-column {
      min-width: 58px;
    }

    &.area-column {
      min-width: 93px;
    }

    &.age-column {
      min-width: 70px;
    }

    &.structure-column {
      min-width: 82px;
    }

    &.parking-column {
      max-width: 60px;
      padding: 0 5px 20px 0;
    }

    &.actions-column {
      min-width: 137px;
    }

    &.username-column {
      min-width: 70px;
    }

    &.organization-column {
      min-width: 70px;
    }

    &.created-at-column {
      display: none;
    }
  }

  .user-actions-display {
    button,
    a,
    div {
      width: 25%;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e7ecf4;

      &:hover {
        background: #fff;
        border-bottom: 1px solid transparent;
        box-shadow: 0 2px 20px 0 rgba(176, 186, 197, 0.2);
      }
    }

    td {
      color: #313541;
      font-size: 14px;
      padding: 20px 10px 20px 0;

      &:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        padding-right: 15px;
      }

      &.slug-column {
        max-width: 100px;
        min-width: 100px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &.created-at-column {
        display: none;
      }
    }

    .property-link {
      color: #313541;
      text-decoration: none;

      &:hover {
        color: #009cea;
      }
    }

    .property-actions {
      white-space: nowrap;

      a:hover {
        width: 100% !important;
      }
    }

    .icon-action {
      color: #7c91a7;
      font-size: 16px;

      &:hover {
        color: #009cea;
      }
    }

    .gallery {
      background: transparent;
      border: none;
    }

    .dropdown-toggle,
    .dropdown-toggle:focus,
    .dropdown-toggle:active {
      background: transparent !important;
      border: none;
      box-shadow: none;
      color: #7c91a7 !important;
      padding: 0;

      &::after {
        display: none;
      }
    }

    .dropdown-toggle:hover {
      background: transparent !important;
      border: none;
      box-shadow: none;
      color: #009cea !important;
    }

    .data-name-column,
    .data-address-column {
      max-width: 170px;
      word-wrap: break-word;
    }
  }
}
</style>

<style lang="scss">
  @media (max-width: 992px) {
    .vuetable-wrapper {
      overflow: hidden;
      overflow-x: scroll;
    }
  }
</style>
